// ─── BACKEND CLIENT ──────────────────────────────────────────────────────
// Transforms frontend state → backend BriefRequest and makes API calls.

import { API_V1, getAuthHeaders, getSessionHeaders } from './config.js';
import { resolveToneHint } from '../../utils/toneMigration.js';
import { normalizeUrl } from '../../utils/textProcessing.js';

// ─── Blog-SEO keywords serializer ──────────────────────────────────────────
// FE-internal {primary, secondary[]} → wire shape.
// - Trims primary.
// - Drops secondary rows with empty trimmed keyword.
// - Coerces volume: '' | null | NaN → null; numeric string → int.
function serializeKeywords(kws) {
  const safe = kws && typeof kws === 'object' ? kws : { primary: '', secondary: [] };
  const primary = (safe.primary || '').trim();
  const secondary = (Array.isArray(safe.secondary) ? safe.secondary : [])
    .map(row => ({
      keyword: (row?.keyword || '').trim(),
      monthly_volume: (() => {
        const v = row?.monthly_volume;
        if (v === '' || v === null || v === undefined) return null;
        const n = typeof v === 'number' ? v : parseInt(v, 10);
        return Number.isFinite(n) ? n : null;
      })(),
    }))
    .filter(row => row.keyword.length > 0);
  return { primary, secondary };
}

// ─── Platform mapping (16 combos) ─────────────────────────────────────────

export function resolvePlatformAndFormat(state) {
  const { activeMode, contentFormat, socialChannel, emailChannel, adsParams } = state;

  switch (activeMode) {
    case 'linkedin-post':
      return contentFormat === 'carousel'
        ? { platform: 'linkedin', content_format: 'carousel' }
        : { platform: 'linkedin', content_format: 'post' };
    case 'blog-seo':
      return { platform: 'blog', content_format: 'seo' };
    case 'email':
      return emailChannel === 'linkedin-newsletter'
        ? { platform: 'linkedin', content_format: 'newsletter' }
        : { platform: 'email', content_format: 'email-classic' };
    case 'landing':
      return { platform: 'landing', content_format: 'landing' };
    case 'case-study':
      return { platform: 'blog', content_format: 'case-study' };
    case 'ads':
      return { platform: 'ads', content_format: adsParams?.channel || 'google-search' };
    case 'social':
      return { platform: socialChannel || 'instagram', content_format: 'post' };
    default:
      return { platform: activeMode, content_format: '' };
  }
}

// ─── Mode-specific content_params ──────────────────────────────────────────

function buildContentParams(state) {
  const { activeMode, contentFormat, linkedInParams, carouselParams, blogParams,
          emailParams, emailChannel, adsParams, caseStudyParams, landingParams,
          sitemapUrl } = state;
  const params = {};

  switch (activeMode) {
    case 'linkedin-post':
      if (contentFormat === 'carousel') {
        Object.assign(params, { slide_count: carouselParams.slideCount });
      } else {
        Object.assign(params, { post_length: linkedInParams.postLength, cta_type: linkedInParams.ctaType });
      }
      break;
    case 'blog-seo':
      Object.assign(params, {
        article_length: blogParams.articleLength,
        seo_intent: blogParams.seoIntent,
        keywords: serializeKeywords(blogParams.keywords),
        ...(sitemapUrl ? { sitemap_url: normalizeUrl(sitemapUrl) } : {}),
      });
      break;
    case 'email':
      Object.assign(params, {
        email_length: emailParams.emailLength,
        ...(emailChannel === 'linkedin-newsletter' ? {
          include_companion_post: emailParams.includeCompanionPost,
          include_cover_prompt: false,
        } : {}),
      });
      break;
    case 'ads':
      Object.assign(params, {
        ads_channel: adsParams.channel,
        ads_objective: adsParams.adsObjective || 'conversions',
        ad_groups: adsParams.adGroups,
        ad_count: adsParams.adCount || '3',
        group_topics: adsParams.groupTopics || [],
      });
      break;
    case 'case-study':
      Object.assign(params, {
        output_style: caseStudyParams.outputStyle,
        focus_area: caseStudyParams.focusArea,
      });
      break;
    case 'landing':
      Object.assign(params, {
        headline_formula: landingParams.headlineFormula,
        ...(landingParams.socialProof?.trim()
          ? { social_proof: landingParams.socialProof.trim() }
          : {}),
      });
      break;
    default:
      break;
  }
  return params;
}

// ─── Build BriefRequest from frontend state ───────────────────────────────

export function buildBriefRequest(state) {
  const { platform, content_format } = resolvePlatformAndFormat(state);

  // Research sources
  const hasUploadedDocs = state.userKbEnabled && (state.uploadBundle?.files?.length > 0);
  const researchSources = {
    use_web_search: state.webSearch.enabled && state.webSearch.useForGeneration,
    web_search_query: state.webSearch.query || state.taskText,
    web_search_max_results: state.webSearch.generationResults || 5,
    use_company_website: state.kbSearch.enabled && state.kbSearch.useForGeneration,
    use_company_docs: state.kbSearch.enabled && state.kbSearch.useForGeneration,
    kb_search_query: state.kbSearch.query || undefined,
    use_uploaded_documents: hasUploadedDocs,
    upload_search_query: state.userKbQuery || undefined,
    request_urls: state.refsEnabled
      ? (state.referenceUrls || []).filter(u => u && u.trim()).map(normalizeUrl)
      : [],
  };

  // Key points: split by newlines, trim, filter empty
  const keyPoints = (state.keyPoints || '')
    .split('\n')
    .map(s => s.trim())
    .filter(s => s);

  return {
    topic: state.taskText,
    platform,
    content_format: content_format || undefined,
    objective: state.objective,
    persona: state.persona,
    research_requested:
      (state.webSearch.enabled && state.webSearch.useForGeneration) ||
      (state.kbSearch.enabled && state.kbSearch.useForGeneration) ||
      (state.refsEnabled && (state.referenceUrls || []).some(u => u && u.trim())) ||
      hasUploadedDocs,
    research_sources: researchSources,
    additional_context: state.focusTopic || undefined,
    key_points: keyPoints,
    tone_hint: resolveToneHint(state),
    cta: state.ctaText || undefined,
    cta_url: state.ctaUrl ? normalizeUrl(state.ctaUrl) : undefined,
    content_params: buildContentParams(state),
    brand_guidelines_id: state.tenant || 'default',
  };
}

// ─── API calls ─────────────────────────────────────────────────────────────

/**
 * Start a pipeline run. Returns { task_id, status }.
 */
export async function startPipelineRun(briefRequest, { tenant, factCheckOverride } = {}) {
  const params = new URLSearchParams();
  if (tenant) params.set('tenant', tenant);
  if (factCheckOverride !== undefined) params.set('fact_check', factCheckOverride);

  const res = await fetch(`${API_V1}/pipeline/run?${params}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getSessionHeaders(),
      ...getAuthHeaders(),
    },
    body: JSON.stringify(briefRequest),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const ref = err.request_id ? ` (ref: ${err.request_id})` : '';
    throw new Error((err.message || `Pipeline start failed: ${res.status}`) + ref);
  }

  return res.json();
}

/**
 * Get pipeline run status.
 */
export async function getPipelineRunStatus(taskId) {
  const res = await fetch(`${API_V1}/pipeline/run/${taskId}/status`, {
    headers: { ...getSessionHeaders(), ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Soft-cancel a running pipeline. No request body.
 * Backend honours the cancel at the next node boundary → terminal status 'cancelled'.
 * Throws Error with `.status` set so callers can branch (409 = styling already started).
 */
export async function cancelPipelineRun(taskId) {
  const res = await fetch(`${API_V1}/pipeline/run/${taskId}/cancel`, {
    method: 'POST',
    headers: { ...getSessionHeaders(), ...getAuthHeaders() },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.message || `Cancel failed: ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return res.json().catch(() => ({}));
}

function getTenantHeader() {
  const tenantId = localStorage.getItem('auth_tenant_id');
  return tenantId ? { 'X-Tenant-Id': tenantId } : {};
}

/**
 * Fetch available personas from the context API.
 * Returns [{ value, label, source }].
 */
export async function fetchPersonas() {
  const res = await fetch(`${API_V1}/context/personas`, {
    headers: { ...getSessionHeaders(), ...getAuthHeaders(), ...getTenantHeader() },
  });
  if (!res.ok) throw new Error(`Personas fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch available objectives from the context API.
 * Returns [{ value, label, source }].
 */
export async function fetchObjectives() {
  const res = await fetch(`${API_V1}/context/objectives`, {
    headers: { ...getSessionHeaders(), ...getAuthHeaders(), ...getTenantHeader() },
  });
  if (!res.ok) throw new Error(`Objectives fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Get generated content for a completed pipeline run.
 */
export async function getPipelineRunContent(taskId) {
  const res = await fetch(`${API_V1}/pipeline/run/${taskId}/content`, {
    headers: { ...getSessionHeaders(), ...getAuthHeaders() },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const ref = err.request_id ? ` (ref: ${err.request_id})` : '';
    throw new Error((err.message || `Content fetch failed: ${res.status}`) + ref);
  }
  return res.json();
}
